import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useListGroup, useListUser } from "../../api/bpm/endpoints";
import type { SmartGroupDto, SmartUserDto } from "../../api/bpm/model";
import { useAuth } from "../../auth";

interface DashboardUserGroupFilterProps {
    userId: string | undefined;
    groupId: string | undefined;
    onChangeUser: (userId: string | undefined) => void;
    onChangeGroup: (groupId: string | undefined) => void;
}

export default function DashboardUserGroupFilter({
    userId,
    groupId,
    onChangeUser,
    onChangeGroup,
}: DashboardUserGroupFilterProps) {
    const { t } = useTranslation();
    const { user: authUser } = useAuth();
    
    const [userInputValue, setUserInputValue] = useState("");
    const [groupInputValue, setGroupInputValue] = useState("");

    const { data: usersResponse, isLoading: isLoadingUsers } = useListUser(
        authUser?.tenant || "",
        { page: 0, size: 100 },
        { query: { enabled: !!authUser?.tenant } },
    );

    const { data: groupsResponse, isLoading: isLoadingGroups } = useListGroup(
        authUser?.tenant || "",
        { page: 0, size: 100 },
        { query: { enabled: !!authUser?.tenant } },
    );

    const users = (usersResponse as any)?.content?.sort((a: any, b: any) =>
        (a.name || "").localeCompare(b.name || ""),
    ) || [];

    const groups = (groupsResponse as any)?.content?.sort((a: any, b: any) =>
        (a.name || "").localeCompare(b.name || ""),
    ) || [];

    const selectedUser = users.find((u: SmartUserDto) => u.id === userId) || null;
    const selectedGroup = groups.find((g: SmartGroupDto) => g.id === groupId) || null;

    return (
        <Box
            data-testid="dashboard-user-group-filter"
            sx={{
                display: "flex",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 1.5,
                width: { xs: "100%", sm: "auto" },
                flexShrink: 1,
                minWidth: 0,
            }}
        >
            <Autocomplete
                options={users}
                getOptionLabel={(option: SmartUserDto) => `${option.name}`.trim()}
                value={selectedUser}
                onChange={(_, newValue) => onChangeUser(newValue?.id || undefined)}
                inputValue={userInputValue}
                onInputChange={(_, newInputValue) => setUserInputValue(newInputValue)}
                loading={isLoadingUsers}
                size="small"
                sx={{ width: { xs: "100%", sm: 160, lg: 180 }, flexShrink: 1, minWidth: 140 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("dashboard.processMetrics.filterUser")}
                        variant="outlined"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {isLoadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }
                        }}
                    />
                )}
            />

            <Autocomplete
                options={groups}
                getOptionLabel={(option: SmartGroupDto) => `${option.name}`.trim()}
                value={selectedGroup}
                onChange={(_, newValue) => onChangeGroup(newValue?.id || undefined)}
                inputValue={groupInputValue}
                onInputChange={(_, newInputValue) => setGroupInputValue(newInputValue)}
                loading={isLoadingGroups}
                size="small"
                sx={{ width: { xs: "100%", sm: 160, lg: 180 }, flexShrink: 1, minWidth: 140 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("dashboard.processMetrics.filterGroup")}
                        variant="outlined"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {isLoadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }
                        }}
                    />
                )}
            />
        </Box>
    );
}
